import React, { useState, useEffect } from 'react';
import { useTenantSettingsStore } from '../../context/TenantSettingsStore';
import InfoBar from './InfoBar';
import { useTranslation } from 'react-i18next';

// Time before maintenance starts to show warning
const MAINTENANCE_WARNING_HOURS = 3;

const SystemMessages = () => {
    const [maintenanceDismissed, setMaintenanceDismissed] = useState(false);
    const { t } = useTranslation();

    const { status, getNextMaintenanceEvent, settings } = useTenantSettingsStore((state) => ({
        status: state.status,
        getNextMaintenanceEvent: state.getNextMaintenanceEvent,
        settings: state.settings
    }));

    const systemMessages = settings?.systemMessages;

    // Reset dismissed state when maintenance warning changes
    useEffect(() => {
        const nextEvent = getNextMaintenanceEvent();
        if (nextEvent && !maintenanceDismissed) {
            setMaintenanceDismissed(false);
        }
    }, [getNextMaintenanceEvent, maintenanceDismissed]);

    // Prepare maintenance warning if needed
    const maintenanceWarning = React.useMemo(() => {
        if (maintenanceDismissed) {
            return null;
        }

        const nextEvent = getNextMaintenanceEvent();
        if (!nextEvent) {
            return null;
        }

        const now = new Date().getTime();
        const startTime = new Date(nextEvent.startTime).getTime();
        const endTime = new Date(nextEvent.endTime).getTime();
        const warningTime = startTime - (MAINTENANCE_WARNING_HOURS * 60 * 60 * 1000);

        // Only show warning if we're within the warning window but before maintenance starts
        const shouldShow = now >= warningTime && now < startTime;

        if (!shouldShow) {
            return null;
        }

        const start = new Date(nextEvent.startTime).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });

        const end = new Date(nextEvent.endTime).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });

        // Calculate duration in hours, rounded to nearest 0.5
        const durationHours = Math.round((endTime - startTime) / (1000 * 60 * 60) * 2) / 2;

        return {
            message: t('maintenance.warning', {
                start,
                end,
                duration: durationHours,
                message: nextEvent.message
            }),
            level: 'info',
            buttonLabel: t('common.buttons.ok')
        };
    }, [maintenanceDismissed, getNextMaintenanceEvent, t]);

    // If still loading, don't show anything yet
    if (status !== 'success') {
        return null;
    }

    // If no messages to show, return null
    if (!maintenanceWarning && !systemMessages?.length) {
        return null;
    }

    return (
        <>
            {maintenanceWarning && (
                <InfoBar
                    {...maintenanceWarning}
                    allowDismiss={true}
                    onDismiss={() => setMaintenanceDismissed(true)}
                />
            )}
            {systemMessages?.map((msg) => (
                <InfoBar
                    key={msg.message}
                    message={msg.message}
                    level={msg.level || 'info'}
                    displayFrom={msg.displayFrom}
                    displayTo={msg.displayTo}
                    buttonLabel={msg.buttonLabel}
                    buttonLink={msg.buttonLink}
                />
            ))}
        </>
    );
};

export default SystemMessages;